import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useEventStore } from 'src/domain/stores/useEventStore';
import { useNavigationStore } from 'src/domain/stores/useNavigationStore';
import { useServicesStore } from 'src/domain/stores/useServicesStore';
import Fonts from 'src/core/utils/Fonts';
import Dimens from 'src/core/utils/Dimens';
import Colors from 'src/core/utils/Colors';
import type { ApiContentBlockList } from 'src/core/models/api/ApiContentBlock';
import type { ApiPage } from 'src/core/models/api/ApiPage';

interface ContentBlockListProps {
	data: ApiContentBlockList;
	onButtonPress?: (type: string, data: ApiPage) => void;
}

const EMAIL_RE = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

// A list-item description like "+31 6 513 905 33" or "info@x.nl" should open the mail/dialer client.
function getContactType(value: string): 'email' | 'phone' | null {
	const trimmed = value.trim();
	if (!trimmed) return null;
	if (EMAIL_RE.test(trimmed)) return 'email';
	if (/^[+\d\s()-]+$/.test(trimmed) && trimmed.replace(/\D/g, '').length >= 7) return 'phone';
	return null;
}

// A list item can only link to another page when the CMS actually stored one.
function targetPageId(pageId: string | undefined): string | null {
	const trimmed = typeof pageId === 'string' ? pageId.trim() : '';
	return trimmed === '' || trimmed === 'Geen' ? null : trimmed;
}

function ContentBlockList({ data, onButtonPress }: ContentBlockListProps) {
	const eventStore = useEventStore();
	const navigationStore = useNavigationStore();
	const { openPhoneNumber, openEmail } = useServicesStore();
	const listItems = data.data?.cb_list_items ?? [];

	function handleButtonPress(page: string) {
		const pages = page.includes('/') ? page.split('/') : [page];
		const inAppPage = eventStore.getPageByID(pages);
		if (!inAppPage) return;
		navigationStore.setNotificationOrigin('');
		onButtonPress?.(inAppPage.type, inAppPage);
	}

	function openContact(type: 'email' | 'phone', value: string) {
		if (type === 'email') {
			openEmail({ email: value }).catch(() => undefined);
		} else {
			openPhoneNumber({ phoneNumber: value }).catch(() => undefined);
		}
	}

	function renderListItems() {
		return listItems.map((listitem, index) => {
			const targetPage = targetPageId(listitem.page_id);
			const isPageAvailable = targetPage !== null;
			const description = listitem.description ?? '';
			const contact = isPageAvailable ? null : getContactType(description);
			const tappable = isPageAvailable || contact !== null;

			return (
				<TouchableOpacity
					disabled={!tappable}
					key={index}
					style={styles.listItem}
					onPress={() => {
						if (targetPage) {
							handleButtonPress(targetPage);
						} else if (contact) {
							openContact(contact, description);
						}
					}}
				>
					<View style={styles.body}>
						<Text style={styles.listItemTitle}>{listitem.name}</Text>
						<Text style={[styles.listItemDescription, contact ? styles.listItemDescriptionLink : null]}>{description}</Text>
					</View>
					{isPageAvailable ? <MaterialIcons name="keyboard-arrow-right" style={styles.arrow} /> : null}
					{contact === 'phone' ? <MaterialIcons name="call" style={styles.contactIcon} /> : null}
					{contact === 'email' ? <MaterialIcons name="email" style={styles.contactIcon} /> : null}
				</TouchableOpacity>
			);
		});
	}

	return (
		<View style={styles.container}>
			<Text style={[styles.listTitle, { color: eventStore.activeTheme.heading_text_color }]}>{data.name}</Text>
			{renderListItems()}
		</View>
	);
}

const styles = StyleSheet.create({
	container: {
		paddingLeft: Platform.OS === 'ios' ? Dimens.padding20 : Dimens.padding16,
		paddingRight: Platform.OS === 'ios' ? Dimens.padding20 : Dimens.padding16,
		paddingTop: Platform.OS === 'ios' ? Dimens.padding20 : 13,
		paddingBottom: Platform.OS === 'ios' ? Dimens.padding20 : 13,
	},
	listItem: {
		flexDirection: 'row',
		alignItems: 'center',
		marginLeft: Dimens.margin0,
		paddingVertical: Dimens.padding8,
	},
	listTitle: {
		fontFamily: Fonts.montserratMedium,
		fontSize: Platform.OS === 'ios' ? Dimens.fontSize20 : Dimens.fontSize17,
		color: Colors.greyHeader,
		paddingBottom: Dimens.padding10,
	},
	listItemTitle: {
		fontSize: Platform.OS === 'ios' ? Dimens.fontSize17 : Dimens.fontSize16,
		color: Colors.greyTextDark,
		fontFamily: Platform.OS === 'ios' ? Fonts.montserratMedium : Fonts.montserratRegular,
		paddingBottom: Dimens.padding5,
	},
	listItemDescription: {
		color: Colors.overlay54,
		fontFamily: Fonts.montserratRegular,
		fontSize: Platform.OS === 'ios' ? Dimens.fontSize12 : Dimens.fontSize14,
	},
	listItemDescriptionLink: {
		color: Colors.secondary,
	},
	arrow: {
		fontSize: Dimens.fontSize23,
		color: Colors.greyIcon,
	},
	contactIcon: {
		fontSize: Dimens.fontSize23,
		color: Colors.secondary,
	},
	body: {
		flex: 1,
	},
});

export default ContentBlockList;
